// Sentry NextJS Documentation: https://docs.sentry.io/platforms/javascript/guides/nextjs/
// Client init: instrumentation-client.ts
// Server init: sentry.server.config.ts
import type { SentryConfig } from './sentry.config.types';

const {
  NEXT_PUBLIC_REALM,
  NEXT_PUBLIC_SENTRY_DSN,
  SENTRY_DSN,
  NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
  NEXT_PUBLIC_SENTRY_DEBUG,
} = process.env;

/**
 * Scanner posten kaputte Multipart-Bodies und erfundene Server-Action-IDs an beliebige
 * Seiten. Next.js meldet das als Exception, ein Fehler des Portals ist es nicht. Ohne
 * Filter überdecken diese Events echte Fehler (STELLENPORTAL-K, -M, -14, -16).
 */
const BOT_NOISE_PATTERNS = [
  /Failed to find Server Action/,
  /Unexpected end of form/,
  /missing final boundary/i,
  /Can't decode form data from body/,
  /Malformed part header/,
];

/**
 * Fehler aus Browser-Extensions und WebViews, die nichts mit dem Portal zu tun haben:
 * Wallet-Extensions (STELLENPORTAL-1H), Android-WebView nach dem Verlassen der Seite
 * (STELLENPORTAL-1J) und Extensions wie Google Translate, die den DOM unter React veraendern
 * (STELLENPORTAL-1F). Dazu der Link-Scanner von Outlook SafeLinks (STELLENPORTAL-4N) und
 * Stylesheets, die nicht laden, weil das Netz weg ist (STELLENPORTAL-3J).
 */
const BROWSER_NOISE_PATTERNS = [
  /Failed to connect to MetaMask/,
  /Java object is gone/,
  /removeChild.*not a child of this node/,
  /Object Not Found Matching Id:\d+, MethodName:update/,
  /Event `Event` \(type=error\) captured as exception/,
];

/**
 * Der Browser hat die Verbindung beendet, bevor die Antwort fertig gestreamt war: Tab
 * geschlossen, weiternavigiert, Funkloch (STELLENPORTAL-37, -3T).
 */
const CLIENT_ABORT_PATTERNS = [/The destination stream closed early/, /^Connection closed\.?$/];

const getSentryConfig = (): SentryConfig => {
  const baseConfig = {
    environment: NEXT_PUBLIC_REALM,
    dsn: NEXT_PUBLIC_SENTRY_DSN || SENTRY_DSN,
    ignoreErrors: [...BOT_NOISE_PATTERNS, ...BROWSER_NOISE_PATTERNS, ...CLIENT_ABORT_PATTERNS],
    tracesSampleRate: NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE
      ? Number.parseFloat(NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE)
      : undefined,
    debug: NEXT_PUBLIC_SENTRY_DEBUG === 'true',
  };

  // Remove undefined values to keep config clean
  return Object.fromEntries(Object.entries(baseConfig).filter(([_, value]) => value !== undefined)) as SentryConfig;
};

export const sentryConfig = getSentryConfig();
